/**
 * Gets an environment variable with a fallback value
 * @param name The name of the environment variable
 * @param fallback The fallback value if the environment variable is not set
 * @returns The value of the environment variable or the fallback
 */
export const getEnvVariable = (name: string, fallback: string = ''): string => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name] || fallback;
  }

  if (typeof window !== 'undefined' && (window as any).env && (window as any).env[name]) {
    return (window as any).env[name];
  }

  return fallback;
};

/**
 * Gets the AppSync GraphQL endpoint from environment variables
 * @returns The AppSync GraphQL endpoint
 */
export const getAppSyncEndpoint = (): string =>
  getEnvVariable(
    'APP_APPSYNC_ENDPOINT',
    'https://uh24pp3r5zcqjingjrrxudfcke.appsync-api.us-west-2.amazonaws.com/graphql',
  );

/**
 * Gets the AppSync API key from environment variables
 * @returns The AppSync API key
 */
export const getAppSyncApiKey = (): string =>
  getEnvVariable('APP_APPSYNC_API_KEY', 'da2-acngarh35nfxxgxifuplskd3z4');

/**
 * Gets the AWS region from environment variables
 * @returns The AWS region
 */
export const getAwsRegion = (): string => getEnvVariable('AWS_REGION', 'us-west-2');

/**
 * Gets the API Gateway URL from environment variables
 * @returns The API Gateway URL
 */
export const getApiGatewayUrl = (): string =>
  process.env.EXPO_PUBLIC_API_GATEWAY_URL || 'https://develop.heartbeat.homes';

/**
 * Gets the Cognito User Pool ID from environment variables
 * @returns The Cognito User Pool ID
 */
export const getCognitoUserPoolId = (): string => {
  const userPoolId = getEnvVariable('AUTH_USER_POOL_ID', '');
  if (userPoolId) return userPoolId;
  return getEnvVariable('COMPILE_COGNITO_USER_POOL_ID', 'us-west-2_KxkCrToDe');
};

/**
 * Gets the Cognito Client ID from environment variables
 * @returns The Cognito Client ID
 */
export const getCognitoClientId = (): string => {
  const clientId = getEnvVariable('AUTH_USER_POOL_CLIENT_ID', '');
  if (clientId) return clientId;
  return getEnvVariable('COMPILE_CLIENT_ID', '14cuvdsbu7d8di4et8n9hgs43j');
};

/**
 * Gets the base URL from environment variables
 * @returns The base URL
 */
export const getBaseUrl = (isWeb: boolean): string => {
  if (isWeb && typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    if (port) {
      return `${protocol}//${hostname}:${port}`;
    }
    return `${protocol}//${hostname}`;
  }

  return getEnvVariable('BASE_URL', 'http://localhost:3000');
};

/**
 * Gets the Identity Pool ID from environment variables
 * @returns The Identity Pool ID
 */
export const getIdentityPoolId = (): string => {
  const identityPoolId = getEnvVariable('AUTH_IDENTITY_POOL_ID', '');
  if (identityPoolId) return identityPoolId;
  return getEnvVariable('IDENTITY_POOL_ID', 'us-west-2:4e7158b4-3601-4609-ba74-417e05ed7de6');
};

/**
 * Gets the Cognito domain from environment variables
 * @returns The Cognito domain
 */
export const getCognitoDomain = (): string => {
  const domain = getEnvVariable('AUTH_DOMAIN', '');
  if (domain) return domain;
  return getEnvVariable('COGNITO_DOMAIN', 'auth.develop.heartbeat.homes');
};

/**
 * Gets the Weather API Gateway from environment variables
 * @returns The Weather API Gateway
 */
export const getWeatherApiGateway = (): string =>
  process.env.EXPO_PUBLIC_WEATHER_API_GATEWAY || 'https://develop.heartbeat.homes/weather';

export const getAirQualityApiGateway = (): string =>
  process.env.EXPO_PUBLIC_AIR_QUALITY_API_GATEWAY || 'https://develop.heartbeat.homes/air-quality';

export const getFirmwareApiGateway = (): string =>
  getEnvVariable('FIRMWARE_API_GATEWAY', 'http://127.0.0.1:3726');
